import { Card } from '@design/components/card/Card';
import { appTableFeatures } from '@design/components/table';
import { useTable } from '@tanstack/react-table';
import { EarningsDrawer } from '@terminal/views/theme-overview/tab-content/earnings/components/earnings-drawer/EarningsDrawer';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ThemeEarningsTable } from '../theme-earnings-table/ThemeEarningsTable';
import { useThemeEarningsDrawer } from '../useThemeEarningsDrawer';
import { useThemeEarningsInsights } from '../useThemeEarningsInsights';
import { useThemeEarningsThemes } from '../useThemeEarningsThemes';
import type { EarningsPresentationVariant } from '../utils';
import { EarningsCompanyCard } from './EarningsCompanyCard';
import styles from './EarningsReportingSummary.module.scss';
import { getReportedEarningsColumns } from './getReportedEarningsColumns';
import { ReportingSummaryEmptyState } from './ReportingSummaryEmptyState';
import { REPORTED_TODAY_COLUMN_LAYOUT } from './reportedTodayColumnLayout';
import type {
  EarningsTopicClickHandler,
  GuidanceDirection,
  ReportedTodayRow,
} from './types';

interface ReportedTodayCardProps {
  rows: ReportedTodayRow[];
  guidanceByEarningsId: Map<string, GuidanceDirection>;
  onTopicClick: EarningsTopicClickHandler;
  variant?: EarningsPresentationVariant;
}

export const ReportedTodayCard = ({
  rows,
  guidanceByEarningsId,
  onTopicClick,
  variant = 'page',
}: ReportedTodayCardProps) => {
  const { t } = useTranslation(['widget']);
  const isPage = variant === 'page';
  const { earningsInsightByEarningsId, isKgFilterLoading } =
    useThemeEarningsInsights({
      earningsIds: rows.map(earning => earning.id),
      isEnabled: isPage,
    });
  const { themesByEarningsId } = useThemeEarningsThemes(
    earningsInsightByEarningsId,
    'recap',
    isPage && !isKgFilterLoading
  );
  const enrichedRows = useMemo(
    () =>
      rows.map(row => ({
        ...row,
        guidance: guidanceByEarningsId.get(row.id) ?? row.guidance,
        earningsDrivers: themesByEarningsId.get(row.id) ?? row.earningsDrivers,
      })),
    [rows, themesByEarningsId, guidanceByEarningsId]
  );
  const { drawerData, onRowClick, onDrawerClose } = useThemeEarningsDrawer(
    earningsInsightByEarningsId
  );

  const columns = getReportedEarningsColumns({
    showScheduledDateSortIndicator: true,
    onTopicClick,
  });

  const table = useTable({
    features: appTableFeatures,
    data: enrichedRows,
    columns,
    getRowId: row => row.id,
    enableSorting: false,
    initialState: {
      pagination: { pageIndex: 0, pageSize: Infinity },
    },
  });

  if (rows.length === 0) {
    return (
      <ReportingSummaryEmptyState
        title={t(
          'widget:themeOverview.tabs.earnings.reportingSummary.reportedToday'
        )}
        message={t(
          'widget:themeOverview.tabs.earnings.reportingSummary.noCompaniesReportedToday'
        )}
        dataTestId="reported-today-card"
      />
    );
  }

  if (variant === 'drawer') {
    return (
      <section
        className={styles.drawerSection}
        data-testid="reported-today-card"
      >
        <h3 className={styles.drawerTitle}>
          {t(
            'widget:themeOverview.tabs.earnings.reportingSummary.reportedToday'
          )}
        </h3>
        <div className={styles.companyCardGrid}>
          {enrichedRows.map(row => (
            <EarningsCompanyCard key={row.id} row={row} variant="reported" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <Card
      size="large"
      className={styles.card}
      data-testid="reported-today-card"
    >
      <EarningsDrawer drawerData={drawerData} onDrawerClose={onDrawerClose} />
      <p className={styles.title}>
        {t('widget:themeOverview.tabs.earnings.reportingSummary.reportedToday')}
      </p>
      <div className={styles.tableWrapper}>
        <ThemeEarningsTable
          table={table}
          columnLayout={REPORTED_TODAY_COLUMN_LAYOUT}
          onRowClick={onRowClick}
          dataTestId="reported-today-table"
        />
      </div>
    </Card>
  );
};
